import React, { useState, useRef, useEffect } from 'react';
import { Volume2, VolumeX } from 'lucide-react';

const AudioPlayer = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);
  const userHasInteracted = useRef(false);
  const manuallyPaused = useRef(false);

  const audioUrl = "/assets/music.mp3";

  const toggleAudio = (e) => {
    e.stopPropagation(); // Prevent event from bubbling
    
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
        manuallyPaused.current = true; // User manually paused
      } else {
        manuallyPaused.current = false; // User wants to play
        audioRef.current.play().then(() => {
          setIsPlaying(true);
        }).catch(() => {});
      }
    }
  };

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.loop = true;
      audioRef.current.volume = 0.3;
    }
  }, []);

  // Play music on first user interaction (only once)
  useEffect(() => {
    const handleFirstInteraction = () => {
      if (userHasInteracted.current) return;
      userHasInteracted.current = true;
      
      // Only auto-play if user hasn't manually paused
      if (!manuallyPaused.current && audioRef.current) {
        audioRef.current.play().then(() => {
          setIsPlaying(true);
        }).catch(() => {});
      }
      
      // Remove all listeners after first interaction
      events.forEach(event => {
        document.removeEventListener(event, handleFirstInteraction);
      });
    };
    
    const events = ['scroll', 'keydown', 'mousemove'];
    events.forEach(event => {
      document.addEventListener(event, handleFirstInteraction, { passive: true });
    });
    
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleFirstInteraction);
      });
    };
  }, []);

  return (
    <>
      <audio ref={audioRef} src={audioUrl} preload="auto" />
      
      {/* Floating audio control button */}
      <button
        onClick={toggleAudio}
        className="fixed bottom-6 left-6 z-40 w-12 h-12 rounded-full glass flex items-center justify-center transition-all duration-300 hover:scale-110 hover:opacity-100 opacity-50 group"
        style={{
          boxShadow: isPlaying ? '0 0 20px rgba(212, 166, 52, 0.4)' : 'none',
        }}
        aria-label={isPlaying ? 'Mute music' : 'Play music'}
      >
        {isPlaying ? (
          <>
            <Volume2 className="w-5 h-5 text-primary" />
            {/* Sound wave animation */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="absolute w-full h-full rounded-full border border-primary/30 animate-ping" style={{ animationDuration: '2s' }} />
            </div>
          </>
        ) : (
          <VolumeX className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
        )}
      </button>
    </>
  );
};

export default AudioPlayer;
